import { useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";
import {
  IconAlertTriangle,
  IconClock,
  IconCurrencyDollar,
  IconMessageOff,
  IconRobot,
} from "@tabler/icons-react";
import {
  useCreateEscalationLevelMutation,
  useDeleteEscalationLevelMutation,
  useEscalationLevelsQuery,
  useEscalationPolicyQuery,
  useUpdateEscalationLevelMutation,
  useUpdateEscalationPolicyMutation,
  type EscalationLevel,
  type EscalationPolicy,
} from "../api/queries/escalationQueries";
import { useEmailTemplatesQuery } from "../api/queries/emailTemplateQueries";
import { useMentionableOrgUsersQuery } from "../api/queries/orgUsersQueries";
import {
  EscalationStepTemplateSection,
  escalationTemplateSummary,
} from "./EscalationStepTemplateSection";
import { InternalRecipientEmailsEditor } from "./InternalRecipientEmailsEditor";

const SUGGESTED_POSITION_CODES = [2, 3, 4, 5] as const;

function formatRecipientSummary(level: EscalationLevel): string {
  if (level.recipient_type === "INTERNAL") {
    if (level.internal_recipient_mode === "SPECIFIC_USER") {
      if (level.internal_recipient_emails?.length) {
        const joined = level.internal_recipient_emails.join(", ");
        return joined.length > 64
          ? `Internal · ${joined.slice(0, 61)}…`
          : `Internal · ${joined}`;
      }
      return "Internal · Add email address(es)";
    }
    return "Internal · Assigned agent(s)";
  }
  const parts: string[] = [];
  if (level.target_position_codes?.length) {
    parts.push(
      `Position ${[...level.target_position_codes].sort((a, b) => a - b).join(", ")}`,
    );
  }
  if (level.include_manual_escalation_contacts) {
    parts.push("Flagged customer contacts");
  }
  if (!parts.length) {
    return "External · Select who receives this step";
  }
  return `External · ${parts.join(" · ")}`;
}

function PositionCodeSelector({
  selected,
  onChange,
}: {
  selected: number[];
  onChange: (codes: number[]) => void;
}) {
  const [customCode, setCustomCode] = useState("");

  const toggle = (code: number) => {
    const next = selected.includes(code)
      ? selected.filter((c) => c !== code)
      : [...selected, code].sort((a, b) => a - b);
    onChange(next);
  };

  const addCustom = () => {
    const code = Number.parseInt(customCode, 10);
    if (!Number.isFinite(code) || code < 1) return;
    if (!selected.includes(code)) {
      onChange([...selected, code].sort((a, b) => a - b));
    }
    setCustomCode("");
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">
        Contacts synced from Priority with matching position codes on each
        customer. Mark others as escalation contacts on the customer page.
      </p>
      <div className="flex flex-wrap gap-2">
        {SUGGESTED_POSITION_CODES.map((code) => {
          const active = selected.includes(code);
          return (
            <button
              key={code}
              type="button"
              onClick={() => toggle(code)}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                active
                  ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
              }`}
            >
              Pos {code}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={1}
          placeholder="Other"
          value={customCode}
          onChange={(e) => setCustomCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
          className="w-20 rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
        />
        <button
          type="button"
          onClick={addCustom}
          className="text-sm text-emerald-700 hover:underline"
        >
          Add position
        </button>
      </div>
      {selected.length > 0 ? (
        <p className="text-xs text-gray-600">
          Sending to contacts at position{" "}
          <span className="font-medium">{selected.join(", ")}</span>
        </p>
      ) : null}
    </div>
  );
}

function ToggleSwitch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:ring-offset-1 ${
        checked ? "bg-emerald-600" : "bg-gray-300"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

function ThresholdInput({
  id,
  value,
  onChange,
  onBlur,
  suffix,
  min = 1,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  suffix: string;
  min?: number;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
      <span>{suffix.split("{n}")[0]}</span>
      <input
        id={id}
        type="number"
        min={min}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="w-20 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-center text-sm font-medium text-gray-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
      />
      <span>{suffix.split("{n}")[1]}</span>
    </div>
  );
}

function EscalationTriggerCard({
  enabled,
  onToggle,
  icon,
  title,
  description,
  children,
}: {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  icon: ReactNode;
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <div
      className={`h-full rounded-xl border p-4 transition-colors ${
        enabled
          ? "border-emerald-200 bg-white shadow-sm"
          : "border-gray-200 bg-gray-50/60"
      }`}
    >
      <div className="flex items-start gap-3">
        <ToggleSwitch checked={enabled} onChange={onToggle} label={title} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className={`rounded-lg p-1.5 ${
                enabled
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-gray-100 text-gray-400"
              }`}
            >
              {icon}
            </span>
            <p className="font-medium text-gray-900">{title}</p>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-gray-500">
            {description}
          </p>
          {enabled && children ? (
            <div className="mt-3 border-t border-gray-100 pt-3">{children}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function EscalationWhenSection({
  policy,
  onUpdate,
}: {
  policy: EscalationPolicy;
  onUpdate: (payload: Partial<EscalationPolicy>) => Promise<void>;
}) {
  const [overdueDays, setOverdueDays] = useState("");
  const [noReplyDays, setNoReplyDays] = useState("");
  const [minAmount, setMinAmount] = useState("");

  useEffect(() => {
    setOverdueDays(
      policy.days_overdue_min != null ? String(policy.days_overdue_min) : "30",
    );
    setNoReplyDays(
      policy.no_reply_days_min != null
        ? String(policy.no_reply_days_min)
        : "14",
    );
    setMinAmount(policy.min_amount != null ? String(policy.min_amount) : "0");
  }, [policy.days_overdue_min, policy.no_reply_days_min, policy.min_amount]);

  const saveOverdue = async () => {
    if (policy.days_overdue_min == null) return;
    const n = Number(overdueDays);
    if (!Number.isFinite(n) || n < 1) return;
    if (n !== policy.days_overdue_min) {
      await onUpdate({ days_overdue_min: n });
    }
  };

  const saveNoReply = async () => {
    if (policy.no_reply_days_min == null) return;
    const n = Number(noReplyDays);
    if (!Number.isFinite(n) || n < 1) return;
    if (n !== policy.no_reply_days_min) {
      await onUpdate({ no_reply_days_min: n });
    }
  };

  const saveMinAmount = async () => {
    if (policy.min_amount == null) return;
    const n = Number(minAmount);
    if (!Number.isFinite(n) || n < 0) return;
    if (n !== policy.min_amount) {
      await onUpdate({ min_amount: n });
    }
  };

  const activeCount = [
    policy.days_overdue_min != null,
    policy.no_reply_days_min != null,
    policy.trigger_on_llm_escalate,
  ].filter(Boolean).length;

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-gray-800">
            When an invoice escalates
          </h3>
          <p className="mt-0.5 text-sm text-gray-500">
            Turn on the conditions that should move an invoice into escalation.
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800">
          {activeCount} active
        </span>
      </div>

      <div className="grid gap-3 rounded-xl border border-gray-200 bg-linear-to-br from-slate-50/80 to-white p-4 sm:grid-cols-2">
        <EscalationTriggerCard
          enabled={policy.days_overdue_min != null}
          onToggle={async (on) => {
            await onUpdate({
              days_overdue_min: on ? Number(overdueDays) || 30 : null,
            });
          }}
          icon={<IconClock size={18} stroke={1.75} />}
          title="Days overdue"
          description="Escalate unpaid invoices past their due date."
        >
          <ThresholdInput
            id="escalation-days-overdue"
            value={overdueDays}
            onChange={setOverdueDays}
            onBlur={saveOverdue}
            suffix="Escalate when overdue by {n} days"
          />
        </EscalationTriggerCard>

        <EscalationTriggerCard
          enabled={policy.no_reply_days_min != null}
          onToggle={async (on) => {
            await onUpdate({
              no_reply_days_min: on ? Number(noReplyDays) || 14 : null,
            });
          }}
          icon={<IconMessageOff size={18} stroke={1.75} />}
          title="No reply"
          description="Escalate when the customer has not responded after you emailed them."
        >
          <ThresholdInput
            id="escalation-no-reply"
            value={noReplyDays}
            onChange={setNoReplyDays}
            onBlur={saveNoReply}
            suffix="No inbound reply for {n} days after last outbound"
          />
        </EscalationTriggerCard>

        <EscalationTriggerCard
          enabled={policy.min_amount != null}
          onToggle={async (on) => {
            await onUpdate({ min_amount: on ? Number(minAmount) || 0 : null });
          }}
          icon={<IconCurrencyDollar size={18} stroke={1.75} />}
          title="Minimum amount"
          description="Require a minimum balance in addition to another enabled trigger."
        >
          <ThresholdInput
            id="escalation-min-amount"
            value={minAmount}
            onChange={setMinAmount}
            onBlur={saveMinAmount}
            suffix="Invoice balance at least {n}"
            min={0}
          />
        </EscalationTriggerCard>

        <EscalationTriggerCard
          enabled={policy.trigger_on_llm_escalate}
          onToggle={async (on) => {
            await onUpdate({ trigger_on_llm_escalate: on });
          }}
          icon={<IconRobot size={18} stroke={1.75} />}
          title="Customer cancel or escalate (AI)"
          description="Escalate when AI detects cancellation intent or an explicit escalate request in email."
        />
      </div>

      <p className="flex items-center gap-2 text-xs text-gray-500">
        <IconAlertTriangle size={14} className="shrink-0 text-amber-600" />
        Minimum amount filters which invoices can escalate. Other enabled triggers
        use{" "}
        <span className="font-medium text-gray-700">
          {policy.entry_mode === "ALL" ? "all" : "any"}
        </span>{" "}
        logic.
      </p>
    </section>
  );
}

function levelToDraft(level: EscalationLevel): StepDraft {
  return {
    name: level.name,
    days_after_previous_level: level.days_after_previous_level,
    recipient_type: level.recipient_type,
    internal_recipient_mode: level.internal_recipient_mode,
    internal_recipient_emails: level.internal_recipient_emails ?? [],
    recipient_user_id: level.recipient_user_id,
    target_position_codes: level.target_position_codes,
    include_manual_escalation_contacts:
      level.include_manual_escalation_contacts,
    auto_send: level.auto_send,
  };
}

type StepDraft = {
  name: string;
  days_after_previous_level: number;
  recipient_type: EscalationLevel["recipient_type"];
  internal_recipient_mode: EscalationLevel["internal_recipient_mode"];
  internal_recipient_emails: string[];
  recipient_user_id: number | null;
  target_position_codes: number[] | null;
  include_manual_escalation_contacts: boolean;
  auto_send: boolean;
};

function isStepDraftDirty(draft: StepDraft, level: EscalationLevel): boolean {
  return (
    draft.name !== level.name ||
    draft.days_after_previous_level !== level.days_after_previous_level ||
    draft.recipient_type !== level.recipient_type ||
    draft.internal_recipient_mode !== level.internal_recipient_mode ||
    JSON.stringify(draft.internal_recipient_emails) !==
      JSON.stringify(level.internal_recipient_emails ?? []) ||
    draft.recipient_user_id !== level.recipient_user_id ||
    JSON.stringify(draft.target_position_codes ?? []) !==
      JSON.stringify(level.target_position_codes ?? []) ||
    draft.include_manual_escalation_contacts !==
      level.include_manual_escalation_contacts ||
    draft.auto_send !== level.auto_send
  );
}

function stepDraftUpdates(
  draft: StepDraft,
  level: EscalationLevel,
): Partial<EscalationLevel> {
  const updates: Partial<EscalationLevel> = {};
  if (draft.name !== level.name) updates.name = draft.name;
  if (draft.days_after_previous_level !== level.days_after_previous_level) {
    updates.days_after_previous_level = draft.days_after_previous_level;
  }
  if (draft.recipient_type !== level.recipient_type) {
    updates.recipient_type = draft.recipient_type;
  }
  if (draft.internal_recipient_mode !== level.internal_recipient_mode) {
    updates.internal_recipient_mode = draft.internal_recipient_mode;
  }
  if (
    JSON.stringify(draft.internal_recipient_emails) !==
    JSON.stringify(level.internal_recipient_emails ?? [])
  ) {
    updates.internal_recipient_emails = draft.internal_recipient_emails;
    if (draft.internal_recipient_emails.length > 0) {
      updates.recipient_user_id = null;
    }
  }
  if (draft.recipient_user_id !== level.recipient_user_id) {
    updates.recipient_user_id = draft.recipient_user_id;
  }
  if (
    JSON.stringify(draft.target_position_codes ?? []) !==
    JSON.stringify(level.target_position_codes ?? [])
  ) {
    updates.target_position_codes = draft.target_position_codes;
  }
  if (
    draft.include_manual_escalation_contacts !==
    level.include_manual_escalation_contacts
  ) {
    updates.include_manual_escalation_contacts =
      draft.include_manual_escalation_contacts;
  }
  if (draft.auto_send !== level.auto_send) updates.auto_send = draft.auto_send;
  return updates;
}

function applyRecipientTypeToDraft(
  draft: StepDraft,
  recipientType: EscalationLevel["recipient_type"],
): StepDraft {
  if (recipientType === "INTERNAL") {
    return {
      ...draft,
      recipient_type: recipientType,
      internal_recipient_mode:
        draft.internal_recipient_mode ?? "ASSIGNED_AGENTS",
      auto_send: true,
      target_position_codes: null,
      include_manual_escalation_contacts: false,
      internal_recipient_emails:
        draft.internal_recipient_mode === "SPECIFIC_USER"
          ? draft.internal_recipient_emails
          : [],
    };
  }
  return {
    ...draft,
    recipient_type: recipientType,
    internal_recipient_mode: null,
    internal_recipient_emails: [],
    recipient_user_id: null,
    auto_send: false,
    target_position_codes: draft.target_position_codes?.length
      ? draft.target_position_codes
      : [2],
    include_manual_escalation_contacts:
      draft.include_manual_escalation_contacts ?? false,
  };
}

function EscalationStepCard({
  level,
  onSave,
  onDelete,
  isSaving,
  templateSummary,
}: {
  level: EscalationLevel;
  onSave: (updates: Partial<EscalationLevel>) => Promise<void>;
  onDelete: () => void;
  isSaving: boolean;
  templateSummary: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState<StepDraft>(() => levelToDraft(level));
  const { data: orgUsers = [] } = useMentionableOrgUsersQuery({
    enabled: expanded,
  });

  useEffect(() => {
    setDraft(levelToDraft(level));
  }, [level]);

  useEffect(() => {
    if (!expanded || orgUsers.length === 0) return;
    if (level.internal_recipient_emails?.length) return;
    if (!level.recipient_user_id) return;
    const user = orgUsers.find((item) => item.id === level.recipient_user_id);
    if (!user?.email) return;
    setDraft((prev) => {
      if (prev.internal_recipient_emails.length > 0) return prev;
      return { ...prev, internal_recipient_emails: [user.email] };
    });
  }, [
    expanded,
    orgUsers,
    level.internal_recipient_emails,
    level.recipient_user_id,
  ]);

  const isDirty = isStepDraftDirty(draft, level);
  const previewLevel: EscalationLevel = { ...level, ...draft };
  const delayLabel =
    previewLevel.days_after_previous_level === 0
      ? "Immediately"
      : `${previewLevel.days_after_previous_level} days later`;
  const recipientSummary = formatRecipientSummary(previewLevel);

  const handleSave = async () => {
    if (
      draft.recipient_type === "INTERNAL" &&
      draft.internal_recipient_mode === "SPECIFIC_USER" &&
      draft.internal_recipient_emails.length === 0
    ) {
      toast.error("Add at least one email address for this internal step");
      return;
    }
    const updates = stepDraftUpdates(draft, level);
    if (!Object.keys(updates).length) return;
    await onSave(updates);
  };

  const handleCancel = () => {
    setDraft(levelToDraft(level));
  };

  return (
    <div className="relative ml-4 border-l-2 border-slate-200 pl-6 pb-6">
      <span className="absolute -left-[9px] top-1 h-4 w-4 rounded-full bg-indigo-600" />
      <button
        type="button"
        className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="min-w-0 pr-3">
          <p className="font-medium text-slate-900">
            Step {level.sequence_order} — {previewLevel.name}
          </p>
          <p className="text-sm text-slate-500">{delayLabel}</p>
          <p
            className={`mt-1 truncate text-xs ${
              recipientSummary.includes("Select who")
                ? "text-amber-700"
                : "text-slate-500"
            }`}
          >
            {recipientSummary}
          </p>
          <p
            className={`mt-1 truncate text-xs ${
              templateSummary === "No email template"
                ? "text-amber-700"
                : "text-slate-500"
            }`}
          >
            {templateSummary}
          </p>
          {isDirty ? (
            <p className="mt-1 text-xs font-medium text-amber-700">
              Unsaved changes
            </p>
          ) : null}
        </div>
        <span className="shrink-0 text-xs text-slate-400">
          {expanded ? "▲" : "▼"}
        </span>
      </button>
      {expanded ? (
        <div className="mt-3 space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <label className="block text-sm">
            <span className="text-slate-600">Name</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5"
              value={draft.name}
              disabled={isSaving}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-600">Days after previous level</span>
            <input
              type="number"
              min={0}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5"
              value={draft.days_after_previous_level}
              disabled={isSaving}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  days_after_previous_level: Number(e.target.value),
                }))
              }
            />
          </label>

          <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-3">
            <label className="block text-sm">
              <span className="font-medium text-slate-700">
                Who receives this step
              </span>
              <select
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-2.5 py-1.5"
                value={draft.recipient_type}
                disabled={isSaving}
                onChange={(e) =>
                  setDraft((prev) =>
                    applyRecipientTypeToDraft(
                      prev,
                      e.target.value as EscalationLevel["recipient_type"],
                    ),
                  )
                }
              >
                <option value="INTERNAL">Internal (your team)</option>
                <option value="ESCALATION_CONTACT">
                  External (customer contacts)
                </option>
              </select>
            </label>

            {draft.recipient_type === "INTERNAL" ? (
              <div className="space-y-3 border-t border-slate-100 pt-3">
                <label className="block text-sm">
                  <span className="text-slate-600">Internal recipient</span>
                  <select
                    className="mt-1 w-full rounded-lg border border-slate-300 px-2.5 py-1.5"
                    value={draft.internal_recipient_mode ?? "ASSIGNED_AGENTS"}
                    disabled={isSaving}
                    onChange={(e) =>
                      setDraft((prev) => ({
                        ...prev,
                        internal_recipient_mode: e.target
                          .value as EscalationLevel["internal_recipient_mode"],
                        internal_recipient_emails:
                          e.target.value === "ASSIGNED_AGENTS"
                            ? []
                            : prev.internal_recipient_emails,
                        recipient_user_id:
                          e.target.value === "ASSIGNED_AGENTS"
                            ? null
                            : prev.recipient_user_id,
                      }))
                    }
                  >
                    <option value="ASSIGNED_AGENTS">
                      Assigned agent(s) for customer
                    </option>
                    <option value="SPECIFIC_USER">
                      Specific email address(es)
                    </option>
                  </select>
                </label>
                {draft.internal_recipient_mode === "SPECIFIC_USER" ? (
                  <div className="text-sm">
                    <span className="text-slate-600">Recipient emails</span>
                    <div className="mt-1">
                      <InternalRecipientEmailsEditor
                        emails={draft.internal_recipient_emails}
                        orgUsers={orgUsers}
                        disabled={isSaving}
                        onChange={(internal_recipient_emails) =>
                          setDraft((prev) => ({
                            ...prev,
                            internal_recipient_emails,
                          }))
                        }
                      />
                    </div>
                  </div>
                ) : null}
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-slate-600">Send automatically</span>
                  <ToggleSwitch
                    checked={draft.auto_send}
                    label="Send automatically"
                    onChange={(checked) =>
                      setDraft((prev) => ({ ...prev, auto_send: checked }))
                    }
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3 border-t border-slate-100 pt-3">
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    Customer position codes
                  </p>
                  <div className="mt-2">
                    <PositionCodeSelector
                      selected={draft.target_position_codes ?? []}
                      onChange={(codes) =>
                        setDraft((prev) => ({
                          ...prev,
                          target_position_codes: codes.length ? codes : null,
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2.5">
                  <div>
                    <p className="text-sm font-medium text-amber-900">
                      Flagged customer contacts
                    </p>
                    <p className="text-xs text-amber-800/80">
                      Also email contacts marked on each customer page (in
                      addition to position levels)
                    </p>
                  </div>
                  <ToggleSwitch
                    checked={draft.include_manual_escalation_contacts}
                    label="Include flagged customer contacts"
                    onChange={(checked) =>
                      setDraft((prev) => ({
                        ...prev,
                        include_manual_escalation_contacts: checked,
                      }))
                    }
                  />
                </div>
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  External emails always require manager approval before
                  sending.
                </p>
              </div>
            )}
          </div>

          <EscalationStepTemplateSection level={level} />

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
            <button
              type="button"
              className="text-sm text-red-600 hover:underline disabled:opacity-50"
              disabled={isSaving}
              onClick={onDelete}
            >
              Delete step
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!isDirty || isSaving}
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!isDirty || isSaving}
                onClick={() => void handleSave()}
              >
                {isSaving ? "Saving…" : "Save step"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function EscalationSettingsTab() {
  const { data: policy } = useEscalationPolicyQuery();
  const { data: levels = [] } = useEscalationLevelsQuery();
  const { data: templates = [] } = useEmailTemplatesQuery();
  const updatePolicy = useUpdateEscalationPolicyMutation();
  const createLevel = useCreateEscalationLevelMutation();
  const updateLevel = useUpdateEscalationLevelMutation();
  const deleteLevel = useDeleteEscalationLevelMutation();

  if (!policy) {
    return (
      <p className="text-sm text-slate-500">Loading escalation settings…</p>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold text-gray-800">Escalation</h2>
        <p className="text-sm text-gray-500">
          Configure when invoices escalate and what happens at each step.
        </p>
      </div>

      <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div>
          <p className="font-medium text-gray-900">Enable escalation</p>
          <p className="text-sm text-gray-500">
            Run escalation rules on unpaid invoices for this organization.
          </p>
        </div>
        <ToggleSwitch
          checked={policy.enabled}
          label="Enable escalation"
          onChange={async (checked) => {
            try {
              await updatePolicy.mutateAsync({ enabled: checked });
              toast.success("Escalation settings updated");
            } catch {
              toast.error("Failed to update escalation");
            }
          }}
        />
      </div>

      <div className={policy.enabled ? "" : "pointer-events-none opacity-50"}>
        <EscalationWhenSection
          policy={policy}
          onUpdate={async (payload) => {
            try {
              await updatePolicy.mutateAsync(payload);
            } catch {
              toast.error("Failed to update trigger");
            }
          }}
        />
      </div>

      <section
        className={`space-y-4 ${policy.enabled ? "" : "pointer-events-none opacity-50"}`}
      >
        <h3 className="text-base font-semibold text-gray-800">
          Escalation steps
        </h3>
        <div>
          {levels.map((level) => (
            <EscalationStepCard
              key={level.id}
              level={level}
              templateSummary={escalationTemplateSummary(level, templates)}
              isSaving={
                updateLevel.isPending &&
                updateLevel.variables?.levelId === level.id
              }
              onSave={async (updates) => {
                try {
                  await updateLevel.mutateAsync({
                    levelId: level.id,
                    ...updates,
                  });
                  toast.success("Step saved");
                } catch {
                  toast.error("Failed to save step");
                }
              }}
              onDelete={() => deleteLevel.mutate(level.id)}
            />
          ))}
        </div>
        <button
          type="button"
          className="rounded-lg border border-dashed border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
          onClick={() =>
            createLevel.mutate({
              name: `Step ${levels.length + 1}`,
              recipient_type: "INTERNAL",
              internal_recipient_mode: "ASSIGNED_AGENTS",
              days_after_previous_level: levels.length === 0 ? 0 : 30,
              auto_send: true,
            })
          }
        >
          + Add step
        </button>
      </section>
    </div>
  );
}
